import { useEffect } from 'react';
import { usePreferencesStore } from '../store/preferencesStore';
import { useAuthStore } from '../store/authStore';

/**
 * Custom hook for managing user preferences
 * Provides easy access to preferences with automatic loading and caching
 */
export function usePreferences(section: string) {
  const {
    preferences,
    isLoading,
    isInitialized,
    loadPreferences,
    getPreference,
    setPreference,
    setSectionPreferences,
    deletePreference,
    resetToDefaults
  } = usePreferencesStore();

  const { isAuthenticated } = useAuthStore();

  // Load preferences when user is authenticated and store is not initialized
  useEffect(() => {
    console.log('usePreferences effect:', { isAuthenticated, isInitialized, isLoading, section });
    if (isAuthenticated && !isInitialized && !isLoading) {
      console.log('Loading preferences for section:', section);
      loadPreferences();
    }
  }, [isAuthenticated, isInitialized, isLoading, loadPreferences, section]);

  // Get preference value with type safety
  const get = <T = any>(key: string, defaultValue?: T): T => {
    return getPreference(section, key, defaultValue);
  };

  // Set single preference
  const set = async (key: string, value: any) => {
    await setPreference(section, key, value);
  };

  // Set multiple preferences at once
  const setMultiple = async (newPreferences: Record<string, any>) => {
    await setSectionPreferences(section, newPreferences);
  };

  // Delete single preference
  const remove = async (key: string) => {
    await deletePreference(section, key);
  };

  // Reset section to defaults
  const reset = async () => {
    await resetToDefaults(section);
  };

  // Get all preferences for the section
  const getAll = () => {
    return preferences[section] || {};
  };

  return {
    // Data
    preferences: getAll(),
    isLoading,
    isInitialized,
    
    // Methods
    get,
    set,
    setMultiple,
    remove,
    reset,
    
    // Utilities
    isReady: isInitialized && !isLoading,
  };
}

/**
 * Hook specifically for Assets section preferences
 */
export function useAssetsPreferences() {
  const preferences = usePreferences('assets');
  
  return {
    ...preferences,
    
    // Typed getters for common assets preferences
    viewMode: preferences.get<'cards' | 'table'>('viewMode', 'cards'),
    visibleColumns: preferences.get<string[]>('visibleColumns', [
      'name', 'type', 'asset_category', 'status', 'value_assessment', 'location', 'review_date', 'actions'
    ]),
    
    // Typed setters
    setViewMode: (mode: 'cards' | 'table') => preferences.set('viewMode', mode),
    setVisibleColumns: (columns: string[]) => preferences.set('visibleColumns', columns),
  };
}

/**
 * Hook specifically for Documents section preferences
 */
export function useDocumentsPreferences() {
  const preferences = usePreferences('documents');
  
  return {
    ...preferences,
    
    // Typed getters for common documents preferences
    viewMode: preferences.get<'cards' | 'table'>('viewMode', 'cards'),
    visibleColumns: preferences.get<string[]>('visibleColumns', [
      'title', 'category', 'status', 'creator_name', 'nis2_article', 'nis2_letter', 'updated_at', 'actions'
    ]),
    
    // Typed setters
    setViewMode: (mode: 'cards' | 'table') => preferences.set('viewMode', mode),
    setVisibleColumns: (columns: string[]) => preferences.set('visibleColumns', columns),
  };
}

/**
 * Hook specifically for Trainings section preferences
 */
export function useTrainingsPreferences() {
  const preferences = usePreferences('trainings');
  
  return {
    ...preferences,
    
    // Typed getters
    viewMode: preferences.get<'cards' | 'table'>('viewMode', 'cards'),
    showCompleted: preferences.get<boolean>('showCompleted', true),
    
    // Typed setters
    setViewMode: (mode: 'cards' | 'table') => preferences.set('viewMode', mode),
    setShowCompleted: (show: boolean) => preferences.set('showCompleted', show),
  };
}
